/**
 * Running cumulative points score — KAN-465
 *
 * Sums earned/possible quiz points over a learner's course progress rows
 * (one row per quiz, best submission — the same quizEarnedPoints /
 * quizTotalPoints the per-quiz badges read) and compares the running
 * percentage to the course passing threshold, so learners can see mid-course
 * whether they have dropped below it.
 *
 * Deliberate semantics:
 *   - Denominator = ATTEMPTED quizzes only. The question the readout answers
 *     is "where do I stand on what I've taken so far", not the backend
 *     course-aggregate which counts untaken quizzes against the learner.
 *   - One row per quiz: callers pass courseProgressMap.values() and every row
 *     is summed. NEVER dedupe by row.moduleId — the player rewrites a
 *     unit-quiz entry's moduleId while the map key stays the unitId, so two
 *     distinct quizzes can share a moduleId value.
 *   - Manual-grant submissions are included: they are the learner's official
 *     standing. EXCEPT prior completion credit (KAN-627, quizPriorCredit):
 *     that is instructor credit for work done elsewhere, not an earned score,
 *     so it neither adds points nor counts as attempted.
 *   - A row is counted ONLY when its points are trustworthy per the shared
 *     shouldShowPoints heuristic (the same rule every per-quiz badge uses).
 *     That excludes: rows without a submission (unit-quiz completion stamps
 *     with no points — never fabricate), zero-point acknowledgement quizzes
 *     (no points at stake), and migration-056 backfill rows whose earned/total
 *     is a percentage in disguise (summing pseudo-points would distort the
 *     tally). When nothing qualifies, the summary is empty and callers hide
 *     the readout — better absent than wrong.
 *
 * Consistency with the rest of the app:
 *   - Trustworthy-points gate: shouldShowPoints (score-display.utils).
 *   - Percentage rounding: roundPercentage (canonical 1-dp HALF_UP).
 *   - Threshold comparison: isQuizPassing on the RAW percentage (KAN-239
 *     boundary semantics — display rounding never alters the comparison).
 *   - Points formatting: formatScore (2-dp HALF_UP, trailing zeros trimmed).
 *   - Override shadowing: overriddenScore + overriddenEarnedPoints both set
 *     replaces the earned value, mirroring formatScoreParts.
 */

import { formatScore } from '../pipes/score-pts.pipe';
import { shouldShowPoints } from './score-display.utils';
import { formatPercentText, isQuizPassing, roundPercentage } from '@shared/utils/quiz-pass.utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RunningScoreSummary {
  /** Number of quizzes with a real points submission counted into the sums. */
  countedQuizzes: number;
  /** Sum of earned points over attempted quizzes (overrides honoured). */
  earnedPoints: number;
  /** Sum of possible points over attempted quizzes. */
  possiblePoints: number;
  /**
   * Running percentage, canonical 1-dp HALF_UP rounding.
   * null when nothing is attempted OR possiblePoints is 0 (all-ack course).
   */
  percent: number | null;
  /** "{earned}/{possible} pts" via formatScore — empty when nothing attempted. */
  pointsText: string;
  /** "{percent}%" — empty when percent is null. */
  percentText: string;
  /** The course passing threshold passed in, normalised to null when unset. */
  threshold: number | null;
  /**
   * true  → running score is below the course passing threshold
   * false → at or above the threshold (canonical 1-dp boundary)
   * null  → no verdict possible (no threshold, nothing attempted, or 0 possible)
   */
  belowThreshold: boolean | null;
}

// ─── Field resolution ────────────────────────────────────────────────────────

/** Parse a number that may arrive as a pg-numeric string. */
function toNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const num = typeof value === 'string' ? parseFloat(value) : (value as number);
  return typeof num === 'number' && Number.isFinite(num) ? num : null;
}

/** Both override fields set — the instructor's adjustment shadows the original (formatScoreParts rule). */
function hasOverride(row: any): boolean {
  return (
    row.overriddenScore !== null &&
    row.overriddenScore !== undefined &&
    row.overriddenEarnedPoints !== null &&
    row.overriddenEarnedPoints !== undefined
  );
}

/** Earned points for a row, honouring instructor overrides. */
function resolveEarnedPoints(row: any): number | null {
  if (hasOverride(row)) {
    return toNumber(row.overriddenEarnedPoints);
  }
  return toNumber(
    row.quizEarnedPoints ?? row.quiz_earned_points ?? row.earnedPoints ?? row.earned_points
  );
}

function resolveTotalPoints(row: any): number | null {
  return toNumber(
    row.quizTotalPoints ?? row.quiz_total_points ?? row.totalPoints ?? row.total_points
  );
}

// ─── computeRunningScore ─────────────────────────────────────────────────────

/**
 * Compute the learner's running cumulative points score across a course.
 *
 * @param rows       Progress rows — pass courseProgressMap.values(). One row
 *                   per quiz; every row with real points is summed.
 * @param threshold  courseData.passingPolicy.coursePassingThreshold, or
 *                   null/undefined when the course does not enforce one.
 */
export function computeRunningScore(
  rows: Iterable<any>,
  threshold?: number | null
): RunningScoreSummary {
  const normalisedThreshold = threshold === null || threshold === undefined ? null : threshold;

  let countedQuizzes = 0;
  let earnedPoints = 0;
  let possiblePoints = 0;

  for (const row of rows) {
    if (row === null || row === undefined) continue;
    if (row.quizPriorCredit === true) continue;
    const earned = resolveEarnedPoints(row);
    const total = resolveTotalPoints(row);
    // Only rows with BOTH values are real submissions — never fabricate points.
    if (earned === null || total === null) continue;
    // Trustworthy-points gate (formatScoreParts's dataForShowPoints pattern):
    // shadow the override values so overridden rows are judged on them.
    const dataForShowPoints = hasOverride(row)
      ? { ...row, quizEarnedPoints: earned, quizScore: row.overriddenScore }
      : row;
    if (!shouldShowPoints(dataForShowPoints)) continue;
    countedQuizzes++;
    earnedPoints += earned;
    possiblePoints += total;
  }

  if (countedQuizzes === 0) {
    return {
      countedQuizzes: 0,
      earnedPoints: 0,
      possiblePoints: 0,
      percent: null,
      pointsText: '',
      percentText: '',
      threshold: normalisedThreshold,
      belowThreshold: null
    };
  }

  const rawPercent = possiblePoints > 0 ? (earnedPoints / possiblePoints) * 100 : null;
  const percent = rawPercent === null ? null : roundPercentage(rawPercent);

  const belowThreshold =
    rawPercent === null || normalisedThreshold === null
      ? null
      : !isQuizPassing(rawPercent, normalisedThreshold);

  return {
    countedQuizzes,
    earnedPoints,
    possiblePoints,
    percent,
    pointsText: `${formatScore(earnedPoints)}/${formatScore(possiblePoints)} pts`,
    percentText: percent === null ? '' : formatPercentText(percent),
    threshold: normalisedThreshold,
    belowThreshold
  };
}
